/**
 * Album Matcher
 * Groups YouTube Music tracks into albums and prepares them for syncing
 */

import { filterUsefulTracks, getUniqueAlbums, type Track } from './youtube-music-parser';

export interface AlbumMatch {
  artist: string;
  album: string;
  trackCount: number;
  tracks: Track[];
}

export class AlbumMatcher {
  /**
   * Match tracks to albums with progress reporting
   */
  async matchTracksToAlbums(
    tracks: Track[],
    onProgress?: (current: number, currentItem: string) => void
  ): Promise<AlbumMatch[]> {
    // Filter out non-useful tracks
    const usefulTracks = filterUsefulTracks(tracks);
    
    if (onProgress) {
      onProgress(0, 'Filtering tracks...');
    }

    // Group by album
    const uniqueAlbums = getUniqueAlbums(usefulTracks);
    
    const matches: AlbumMatch[] = [];
    
    for (let i = 0; i < uniqueAlbums.length; i++) {
      const albumInfo = uniqueAlbums[i];
      
      if (onProgress) {
        onProgress(
          i + 1,
          `Processing: ${albumInfo.artist} - ${albumInfo.album}`
        );
      }

      // Find all tracks for this album
      const albumTracks = usefulTracks.filter(
        track =>
          track.artist.toLowerCase() === albumInfo.artist.toLowerCase() &&
          track.album.toLowerCase() === albumInfo.album.toLowerCase()
      );

      matches.push({
        artist: albumInfo.artist,
        album: albumInfo.album,
        trackCount: albumInfo.trackCount,
        tracks: albumTracks,
      });

      // Small delay to prevent UI blocking
      if (i % 10 === 0) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }

    // Sort by track count (albums with more tracks first)
    matches.sort((a, b) => b.trackCount - a.trackCount);

    return matches;
  }

  /**
   * Calculate similarity between two strings (0-1, where 1 is identical)
   */
  private calculateSimilarity(str1: string, str2: string): number {
    const s1 = str1.toLowerCase().trim();
    const s2 = str2.toLowerCase().trim();

    if (s1 === s2) return 1;

    // Levenshtein distance
    const matrix: number[][] = [];

    for (let i = 0; i <= s2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= s1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= s2.length; i++) {
      for (let j = 1; j <= s1.length; j++) {
        if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    const distance = matrix[s2.length][s1.length];
    const maxLength = Math.max(s1.length, s2.length);
    
    return maxLength === 0 ? 1 : 1 - distance / maxLength;
  }

  /**
   * Clean album/artist names for better matching
   */
  private cleanName(name: string): string {
    return name
      .toLowerCase()
      .replace(/\(.*?\)/g, '') // Remove parentheses and content
      .replace(/\[.*?\]/g, '') // Remove brackets and content
      .replace(/[^\w\s]/g, '') // Remove special characters
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
  }

  /**
   * Check if two album names are likely the same
   */
  isAlbumMatch(album1: string, artist1: string, album2: string, artist2: string): boolean {
    const cleanAlbum1 = this.cleanName(album1);
    const cleanAlbum2 = this.cleanName(album2);
    const cleanArtist1 = this.cleanName(artist1);
    const cleanArtist2 = this.cleanName(artist2);

    // Check exact match after cleaning
    if (cleanAlbum1 === cleanAlbum2 && cleanArtist1 === cleanArtist2) {
      return true;
    }

    // Check similarity
    const albumSimilarity = this.calculateSimilarity(cleanAlbum1, cleanAlbum2);
    const artistSimilarity = this.calculateSimilarity(cleanArtist1, cleanArtist2);

    // Consider it a match if both are very similar
    return albumSimilarity >= 0.85 && artistSimilarity >= 0.85;
  }

  /**
   * Deduplicate album matches
   */
  deduplicateMatches(matches: AlbumMatch[]): AlbumMatch[] {
    const deduplicated: AlbumMatch[] = [];
    const seen = new Set<string>();

    for (const match of matches) {
      const key = `${this.cleanName(match.artist)}-${this.cleanName(match.album)}`;
      
      if (!seen.has(key)) {
        seen.add(key);
        deduplicated.push(match);
      }
    }

    return deduplicated;
  }

  /**
   * Get statistics about the matching process
   */
  getMatchStatistics(tracks: Track[], matches: AlbumMatch[]): {
    totalTracks: number;
    usefulTracks: number;
    uniqueAlbums: number;
    averageTracksPerAlbum: number;
    mostTracksInAlbum: number;
  } {
    const usefulTracks = filterUsefulTracks(tracks);
    const trackCounts = matches.map(m => m.trackCount);
    
    return {
      totalTracks: tracks.length,
      usefulTracks: usefulTracks.length,
      uniqueAlbums: matches.length,
      averageTracksPerAlbum: trackCounts.length > 0 
        ? Math.round(trackCounts.reduce((a, b) => a + b, 0) / trackCounts.length)
        : 0,
      mostTracksInAlbum: trackCounts.length > 0 ? Math.max(...trackCounts) : 0,
    };
  }
}
